import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../../../SleepHub/sleepHubApi';
import { fetchCompanyVisionBoardDashboard } from './companyVisionBoardApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  BigStatPanel,
  ProgressList,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID,
  CHART_GRID_NO_X
} from '../../../../component/common/designSystem';
import { BOARD_SIZE_RAMP, VOLUME_RAMP, REMINDER_RAMP, FILL_RAMP } from './visionBoardTheme';

/**
 * Vision Board → Dashboard.
 *
 * Three things carry the page:
 *
 *  1. EMPTY BOARDS. Making a board is one tap; putting something on it is the
 *     act that means anything. A board with nothing on it is somebody who
 *     opened the feature and walked away, and it is the only abandonment signal
 *     this feature produces — there is no status field to read. So it gets a
 *     card, a bar and a row of its own rather than hiding inside an average.
 *  2. REMINDERS. The one thing on a board that says the person intends to come
 *     back to it. The share carrying one, and the time of day they pick, are as
 *     close as this feature gets to depth of use.
 *  3. WHICH SHOORAH IDEAS START BOARDS. The only content on this page, and it
 *     is Shoorah's own catalogue copy — read from the catalogue, not from the
 *     board.
 *
 * What anybody actually wrote is not here and not reachable from here. A vision
 * board is a list of someone's private hopes — leaving a job, having a baby,
 * getting out of debt — so the titles, the words and photos on the board, the
 * stories and the tags are never read by any endpoint behind this page.
 *
 * `fetcher` is a prop so the super-admin Vision Board hub can render this page
 * unscoped; the builders behind both are the same.
 */
const VisionBoardDashboard = ({
  fetcher = fetchCompanyVisionBoardDashboard,
  populationLabel = 'users'
}) => {
  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  const boardSizeSplit = stats?.boardSizeSplit || [];
  const volumeSplit = stats?.volumeSplit || [];
  const reminderBands = stats?.reminderBands || [];
  const itemMix = stats?.itemMix || [];
  const topIdeas = stats?.topIdeas || [];
  const weekdayBoards = stats?.weekdayBoards || [];
  const boardsTrend = stats?.boardsTrend || [];
  const peopleTrend = stats?.peopleTrend || [];

  // What share of the population has ever made a board.
  const reachPercentage =
    stats?.totalUsers > 0 ? Math.round((stats.usersActive / stats.totalUsers) * 100) : null;

  const trendHasData = boardsTrend.some((val) => val > 0);

  /**
   * Empty against filled, as one bar. Every board is in exactly one of the two,
   * so the segments always add up to the board count.
   */
  const fillOptions = {
    chart: { ...CHART_BASE, type: 'bar', stacked: true, stackType: '100%' },
    colors: FILL_RAMP,
    plotOptions: { bar: { horizontal: true, borderRadius: 3, barHeight: '45%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID_NO_X,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: {
      categories: ['Boards created'],
      labels: { formatter: (val) => `${Math.round(val)}%` }
    },
    tooltip: { shared: true, y: { formatter: (val) => `${val.toLocaleString()} boards` } }
  };

  const trendOptions = {
    chart: { ...CHART_BASE, type: 'line', stacked: false },
    colors: [CHART_COLORS.brand, CHART_COLORS.teal],
    stroke: { width: [0, 3], curve: 'smooth' },
    plotOptions: { bar: { columnWidth: '65%', borderRadius: 2 } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: lastNDaysLabels(boardsTrend.length), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { shared: true }
  };

  const weekdayOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.teal],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '50%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: weekdayBoards.map((day) => day.label) },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} boards` } }
  };

  return (
    <div>
      {loader ? <Loader /> : null}

      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title="People Making Boards"
          value={stats ? formatBigNumber(stats.usersActive) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.catalog}
          title="Boards Created"
          value={stats ? formatBigNumber(stats.totalBoards) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.custom}
          title="Things On Them"
          value={stats ? formatBigNumber(stats.totalItems) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Boards Filled In"
          value={stats?.totalBoards > 0 ? `${stats.filledRate}%` : '-'}
        />
        {/* The actionable one: opened the feature, never put anything on it. */}
        <StatCard
          icon={STAT_ICONS.more}
          title="Empty Boards"
          value={stats ? formatBigNumber(stats.emptyBoards) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.logs}
          title="Items per Board"
          value={stats ? stats.avgItemsPerBoard : '-'}
        />
      </div>

      {/* ---------- Shape of use ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        {/* Empty boards are deliberately absent from this one — they are not a
            size, they are the card above. */}
        <MiniDonutCard
          title="Board Size"
          subtitle="Items on boards that have any"
          data={boardSizeSplit}
          colors={BOARD_SIZE_RAMP}
          unit="boards"
          totalLabel="Boards"
          legendLimit={4}
        />
        <MiniDonutCard
          title="How Many They Keep"
          subtitle="Boards per person"
          data={volumeSplit}
          colors={VOLUME_RAMP}
          unit="people"
          totalLabel="People"
          legendLimit={4}
        />
        <MiniDonutCard
          title="Reminder Time"
          subtitle="When people ask to be nudged"
          data={reminderBands}
          colors={REMINDER_RAMP}
          unit="boards"
          totalLabel="Reminders"
          legendLimit={4}
        />
      </div>

      {/* ---------- Trend and the abandonment split ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Boards Created Over Time" subtitle="Boards started — last 30 days">
          {trendHasData ? (
            <Chart
              type="line"
              height={380}
              width="100%"
              series={[
                { name: 'Boards', type: 'column', data: boardsTrend },
                { name: 'People', type: 'line', data: peopleTrend }
              ]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>No boards created in the last 30 days.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Started or Filled In" subtitle="Every board is in exactly one of the two">
          {stats?.totalBoards > 0 ? (
            <>
              <Chart
                type="bar"
                height={180}
                width="100%"
                series={[
                  { name: 'Nothing on them', data: [stats.emptyBoards] },
                  { name: 'Filled in', data: [stats.filledBoards] }
                ]}
                options={fillOptions}
              />
              <PanelTable
                head={
                  <>
                    <th className="py-2 pr-4 font-medium">Measure</th>
                    <th className="py-2 font-medium text-right">Boards</th>
                  </>
                }
              >
                <tr className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">Boards created</td>
                  <td className="py-2 text-right">{formatBigNumber(stats.totalBoards)}</td>
                </tr>
                <tr className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">Nothing on them</td>
                  <td className="py-2 text-right">{formatBigNumber(stats.emptyBoards)}</td>
                </tr>
                <tr className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">Filled in</td>
                  <td className="py-2 text-right">{formatBigNumber(stats.filledBoards)}</td>
                </tr>
                {/* Archived is a separate axis: a board can be archived and full.
                    It sits below the split rather than inside it. */}
                <tr className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">Archived</td>
                  <td className="py-2 text-right">{formatBigNumber(stats.archivedBoards)}</td>
                </tr>
              </PanelTable>
            </>
          ) : (
            <PanelEmpty>Nobody has made a board yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Ideas and what goes on a board ---------- */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        {/* The only text on this page. It is Shoorah's own approved catalogue
            copy, read from the catalogue — never anything the person wrote. */}
        <Panel title="Ideas That Start Boards" subtitle="Shoorah prompts your people build from">
          {topIdeas.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Idea</th>
                  <th className="py-2 px-4 font-medium text-center">Boards</th>
                  <th className="py-2 font-medium text-right">People</th>
                </>
              }
            >
              {topIdeas.map((row) => (
                <tr key={row.label} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{row.label}</td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.boards)}</td>
                  <td className="py-2 text-right">{formatBigNumber(row.users)}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No boards have been built from a Shoorah idea yet.</PanelEmpty>
          )}
        </Panel>

        <Panel
          title="What Goes On a Board"
          subtitle="Words or photos — counts only, never the content"
        >
          {stats?.totalItems > 0 ? (
            <>
              <ProgressList items={itemMix} unit="items" color={CHART_COLORS.brand} />
              <PanelTable
                head={
                  <>
                    <th className="py-2 pr-4 font-medium">Measure</th>
                    <th className="py-2 font-medium text-right">Value</th>
                  </>
                }
              >
                <tr className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">Boards started from an idea</td>
                  <td className="py-2 text-right">
                    {formatBigNumber(stats.boardsFromIdea)} ({stats.ideaRate}%)
                  </td>
                </tr>
                <tr className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">Boards with a reminder</td>
                  <td className="py-2 text-right">
                    {formatBigNumber(stats.boardsWithReminder)} ({stats.reminderRate}%)
                  </td>
                </tr>
                {/* Reaching for a stock image rather than one of their own is
                    the low-effort path through the picker. */}
                <tr className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">Photos that are stock images</td>
                  <td className="py-2 text-right">
                    {formatBigNumber(stats.stockPhotos)} ({stats.stockPhotoRate}%)
                  </td>
                </tr>
              </PanelTable>
            </>
          ) : (
            <PanelEmpty>Nothing has been added to a board yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Reach and supporting detail ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        <BigStatPanel
          title="Vision Board Reach"
          subtitle={`Share of ${populationLabel} who have made a board`}
          value={reachPercentage != null ? `${reachPercentage}%` : '-'}
          caption={
            stats
              ? `${formatBigNumber(stats.usersActive)} of ${formatBigNumber(
                  stats.totalUsers
                )} ${populationLabel}`
              : null
          }
        />

        <Panel title="Boards by Weekday" subtitle="When people sit down and make one">
          {weekdayBoards.some((day) => day.count > 0) ? (
            <Chart
              type="bar"
              height={280}
              width="100%"
              series={[{ name: 'Boards', data: weekdayBoards.map((day) => day.count) }]}
              options={weekdayOptions}
            />
          ) : (
            <PanelEmpty>No boards created yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Board Summary" subtitle="The raw counts behind this page">
          <PanelTable
            head={
              <>
                <th className="py-2 pr-4 font-medium">Measure</th>
                <th className="py-2 font-medium text-right">Value</th>
              </>
            }
          >
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Active boards</td>
              <td className="py-2 text-right">
                {stats ? formatBigNumber(stats.activeBoards) : '-'}
              </td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Boards per person</td>
              <td className="py-2 text-right">{stats ? stats.avgBoardsPerUser : '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Empty boards</td>
              <td className="py-2 text-right">
                {stats ? formatBigNumber(stats.emptyBoards) : '-'}
              </td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">First board made</td>
              <td className="py-2 text-right">{stats?.firstBoardDate || '-'}</td>
            </tr>
            <tr className={PANEL_ROW_CLASS}>
              <td className="py-2 pr-4">Last board made</td>
              <td className="py-2 text-right">{stats?.lastBoardDate || '-'}</td>
            </tr>
          </PanelTable>
        </Panel>
      </div>
    </div>
  );
};

VisionBoardDashboard.propTypes = {
  fetcher: PropTypes.func,
  populationLabel: PropTypes.string
};

export default VisionBoardDashboard;
